/**
 * Founder 2026-10-29 — the coverage card names the detected platform with
 * its mark (Shopify, WooCommerce, WordPress) instead of a mono pill, plus
 * the Cloudflare mark when the site runs through Cloudflare.
 *
 * Marks come from `simple-icons` (CC0 SVG data; brand owners keep their
 * trademark rights, display is nominative), the same source as the agent
 * marks in `views/mandates/brand-marks.ts`. Brand colours go through
 * `readableBrandFill` so a dark mark never vanishes on the dark surface.
 * Platforms without a mark keep the existing pill.
 */

import { siCloudflare, siShopify, siWoocommerce, siWordpress } from 'simple-icons';
import type { InstallCoverage } from '@sill/types/api';

import { useC, useTheme } from '../../lib/theme.js';
import { readableBrandFill } from '../mandates/AgentLogo.js';
import type { SimpleIcon } from '../mandates/brand-marks.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

const PLATFORM_MARKS: Readonly<Record<string, SimpleIcon>> = {
  shopify: siShopify,
  woocommerce: siWoocommerce,
  wordpress: siWordpress,
};

/** The mark for a coverage payload, or null when the platform has none. */
export function platformMarkFor(c: Pick<InstallCoverage, 'platform' | 'vendor_hint'>): SimpleIcon | null {
  if (c.vendor_hint !== null) return null;
  return PLATFORM_MARKS[c.platform] ?? null;
}

function Mark({ icon, size }: { readonly icon: SimpleIcon; readonly size: number }): JSX.Element {
  const { theme } = useTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ flexShrink: 0 }}>
      <path d={icon.path} fill={readableBrandFill(icon.hex, theme)} />
    </svg>
  );
}

export function PlatformMark({
  coverage,
  fallbackLabel,
}: {
  readonly coverage: Pick<InstallCoverage, 'platform' | 'vendor_hint' | 'cdn'>;
  /** The existing stack label, shown as the pill when there is no mark. */
  readonly fallbackLabel: string;
}): JSX.Element {
  const C = useC();
  const icon = platformMarkFor(coverage);
  if (icon === null) {
    return (
      <span
        data-testid="coverage-stack"
        style={{
          fontFamily: monoFont, fontSize: 9, letterSpacing: 0.8, fontWeight: 500, whiteSpace: 'nowrap',
          color: C.muted, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2, padding: '2px 8px',
        }}
      >
        {fallbackLabel.toUpperCase()}
      </span>
    );
  }
  const on_cloudflare = coverage.cdn === 'cloudflare';
  return (
    <span
      data-testid="coverage-stack"
      data-platform={coverage.platform}
      className="inline-flex items-center"
      style={{ gap: 6, fontFamily: sansFont, fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}
    >
      <Mark icon={icon} size={16} />
      <span>{icon.title}</span>
      {on_cloudflare ? (
        <>
          <span aria-hidden="true" style={{ color: C.dim }}>·</span>
          <Mark icon={siCloudflare} size={16} />
          <span>Cloudflare</span>
        </>
      ) : null}
    </span>
  );
}
